/**
 * Testi della pagina preventivo, in italiano e inglese.
 *
 * Perché un dizionario locale e non `src/translations`: quel file è il dizionario
 * dell'applicazione operatore, indicizzato con `cap.language` (la lingua scelta
 * dall'utente loggato). Questa pagina è pubblica, la apre il **viaggiatore** e la sua
 * lingua arriva dal preventivo, non dalle preferenze dell'operatore. Tenere i due
 * dizionari separati evita di far dipendere una pagina pubblica dallo stato dell'app e
 * mantiene le chiavi leggibili (`pin.title` invece di `gqPinTitle`).
 *
 * ⚠️ Le due sezioni devono restare identiche chiave per chiave.
 */

export const LANGUAGES = ['it', 'en', 'fr']

/** Locale da usare per date, ore e numeri in ciascuna lingua. */
export const LOCALES = { it: 'it-IT', en: 'en-GB', fr: 'fr-FR' }

const it = {
  languageName: 'Italiano',
  toolbar: {
    language: 'Lingua',
    toLight: 'Passa al tema chiaro',
    toDark: 'Passa al tema scuro'
  },
  header: {
    title: 'Preventivo di viaggio',
    subtitle: 'Powered by Nora',
    validUntil: 'Valido fino al',
    atHour: 'ore'
  },
  loading: 'Caricamento del preventivo…',
  unavailable: {
    title: 'Preventivo non disponibile',
    text: 'Non è stato possibile completare la richiesta.',
    hint: 'Se hai bisogno di assistenza, contatta la tua agenzia di viaggio.'
  },
  // Il conto alla rovescia non è un vezzo: il preventivo vive quindici minuti perché tanto
  // vivono le tariffe, e senza timer il viaggiatore scopre di aver perso tempo solo alla fine.
  deadline: {
    label: 'Tempo per prenotare:',
    hint: 'le tariffe restano disponibili per pochi minuti'
  },
  expired: {
    title: 'Preventivo scaduto',
    text: 'Le tariffe di viaggio restano prenotabili solo per pochi minuti dalla ricerca, e questo preventivo ha superato quel tempo.',
    hint: 'Rispondi al messaggio di Nora e ne prepariamo subito uno aggiornato.'
  },
  footnote:
    'Le offerte sono soggette a disponibilità: i prezzi possono variare al momento della prenotazione.',
  steps: {
    aria: 'Avanzamento della prenotazione',
    choose: '1. Scegli i servizi',
    data: '2. Dati richiesti',
    confirm: '3. Conferma'
  },
  pin: {
    title: 'Inserisci il codice di verifica',
    text: 'Ti abbiamo inviato un codice di 5 cifre. Inseriscilo per aprire il preventivo.',
    inputLabel: 'Codice di verifica',
    attemptsLeft: (n) => `Tentativi rimasti: ${n}`,
    verifying: 'Verifica…',
    submit: 'Apri il preventivo'
  },
  services: {
    air: 'Voli',
    rail: 'Treni',
    hotel: 'Hotel',
    car: 'Auto',
    offers: (n) => `${n} ${n === 1 ? 'offerta' : 'offerte'}`,
    noChoice: 'nessuna scelta',
    leg: (n) => `Tratta ${n}`
  },
  offer: {
    select: 'Seleziona',
    selected: '✓ Selezionato',
    deselectAria: (summary) => `Deseleziona ${summary}`,
    selectAria: (summary) => `Seleziona ${summary}`,
    refundable: 'Rimborsabile',
    nonRefundable: 'Non rimborsabile',
    distanceFromSearch: (km) => `${km} km dal centro ricerca`,
    rating: (value) => `Rating ${value}/10`,
    outbound: 'Andata',
    return: 'Ritorno',
    perDay: 'al giorno',
    moreDetails: 'Dettagli',
    lessDetails: 'Nascondi dettagli',
    cabin: 'Cabina',
    fare: 'Tariffa',
    carrier: 'Vettore',
    stop: 'scalo',
    stops: 'scali',
    via: 'via',
    change: 'cambio',
    changes: 'cambi',
    refundableLabel: 'Rimborsabilità',
    exchangeableLabel: 'Modificabile',
    yes: 'Sì',
    no: 'No',
    totalDuration: 'Durata totale',
    address: 'Indirizzo',
    mealPlan: 'Trattamento',
    ratingLabel: 'Valutazione',
    rentalCompany: 'Compagnia',
    pickupStation: 'Ritiro',
    returnStation: 'Riconsegna',
    transmission: 'Trasmissione',
    fuel: 'Carburante',
    airConditioning: 'Aria condizionata',
    route: 'Tratta',
    pickup: 'Ritiro',
    // Sezioni del pannello dettagli
    sectionItinerary: 'Itinerario',
    sectionFare: 'Tariffa e condizioni',
    sectionProperty: 'Struttura',
    sectionRoom: 'Camera',
    sectionServices: 'Servizi',
    sectionVehicle: 'Veicolo',
    sectionStations: 'Ritiro e riconsegna',
    sectionPrice: 'Prezzo',
    // Segnali in evidenza
    separateTickets: 'Biglietti separati',
    separateTicketsHint:
      "La prenotazione produce biglietti distinti: un ritardo su una tratta non protegge l'altra.",
    mixedCabin: 'Cabina mista',
    mixedCabinHint: 'Le tratte di questo volo non hanno tutte la stessa classe.',
    lowCost: 'Low cost',
    favourite: 'Struttura preferita',
    accessibleRoom: 'Camera accessibile',
    // Aereo e treno
    operatedBy: (carrier) => `operato da ${carrier}`,
    layover: (duration) => `scalo ${duration}`,
    changeAt: 'Cambio a',
    wifiOnBoard: 'Wi-Fi a bordo',
    provider: 'Fornitore',
    cancellationPolicy: 'Cancellazione',
    // Hotel
    chain: 'Catena',
    roomType: 'Tipo camera',
    reviews: (n) => `${n} recensioni`,
    localTax: 'Tasse locali',
    localTaxHint: 'Da pagare in struttura, in aggiunta al prezzo mostrato.',
    ratingCleanliness: 'Pulizia',
    ratingStaffService: 'Personale',
    ratingAmenities: 'Servizi',
    ratingPropertyConditions: 'Struttura',
    distanceFromAnchor: (km) => `${km} km dal punto richiesto`,
    // Auto
    equipment: 'Equipaggiamento',
    perDayShort: 'giorno',
    days: (n) => (n === 1 ? '1 giorno' : `${n} giorni`),
    priceBreakdown: (perDay, days) => `${perDay} × ${days}`,
    estimatedTotal: 'Totale stimato'
  },
  tags: {
    bestPrice: 'Miglior prezzo',
    closest: 'Più rapido',
    bestBuy: 'Best buy',
    nonCompliant: '⚠ Fuori policy'
  },
  cart: {
    title: 'Il tuo viaggio',
    empty:
      'Scegli i servizi che vuoi prenotare: uno per tratta, e li prenotiamo come un unico viaggio.',
    segment: { Air: 'Volo', Rail: 'Treno', Hotel: 'Hotel', Car: 'Auto' },
    total: 'Totale',
    submitting: 'Invio in corso…',
    continueWith: (n) => `Prosegui con ${n} servizi`,
    continueOne: 'Prosegui con il servizio scelto',
    continue: 'Prosegui',
    missingLegs: (n) => `Seleziona ancora ${n} ${n === 1 ? 'servizio' : 'servizi'} per proseguire.`,
    note: 'Il fornitore potrebbe chiedere altri dati prima di confermare.',
    taxesExcluded: 'Il totale non comprende le tasse locali, da pagare in struttura.'
  },
  waiting: {
    preparingTitle: 'Stiamo preparando la prenotazione',
    preparingText:
      'Ancora un passaggio: se il fornitore richiede altri dati, qui sotto comparirà il modulo da compilare.',
    finalizingTitle: 'Stiamo confermando la prenotazione',
    finalizingText: 'Ci siamo quasi: appena il fornitore risponde vedrai il codice viaggio.',
    preparingSteps: [
      'Servizi scelti inviati',
      'Creazione del viaggio',
      'Verifica dei dati richiesti'
    ],
    finalizingSteps: ['Dati inviati', 'Conferma presso il fornitore', 'Codice viaggio'],
    stalledTitle: 'Ci sta mettendo più del previsto',
    stalledText:
      'La prenotazione è comunque in corso e non serve rifarla. Puoi lasciare questa pagina aperta o riaprire il link più tardi: l’esito comparirà qui.',
    stalledHint: 'Se non cambia nulla nei prossimi minuti, contatta la tua agenzia di viaggio.'
  },
  result: {
    pendingTitle: 'Prenotazione in attesa di approvazione',
    pendingText: 'La richiesta è stata inoltrata per l’approvazione aziendale.',
    confirmedTitle: 'Prenotazione confermata',
    confirmedText: 'Il viaggio è stato prenotato con successo.',
    tripCode: 'Codice viaggio:',
    openPortal: 'Visualizza il viaggio nel portale'
  },
  form: {
    introTitle: 'Manca ancora un passaggio',
    introText: (n) =>
      `Il fornitore richiede ${n === 1 ? 'un dato' : `${n} dati`} per emettere questa prenotazione. Compila i campi qui sotto e conferma: il viaggio non è ancora prenotato.`,
    selectPlaceholder: 'Seleziona…',
    missing: (fields) => `Compila i campi obbligatori: ${fields}.`,
    submitting: 'Invio in corso…',
    submit: 'Conferma prenotazione',
    groups: {
      traveller: 'Dati del viaggiatore',
      passport: 'Passaporto',
      identity: 'Documento di identità',
      drivingLicence: 'Patente',
      address: 'Indirizzo',
      company: 'Dati aziendali',
      conditions: 'Condizioni di vendita'
    },
    fields: {
      dateOfBirth: 'Data di nascita',
      title: 'Titolo',
      nationality: 'Nazionalità (sigla, es. IT)',
      phoneCountryCode: 'Prefisso internazionale',
      phoneNumber: 'Numero di telefono (senza prefisso)',
      selectedPassportNo: 'Passaporto da utilizzare',
      'document.documentNumber': 'Numero del documento',
      'document.documentHolder': 'Intestatario come riportato sul documento',
      'document.effectiveDate': 'Data di rilascio',
      'document.expiryDate': 'Data di scadenza',
      'document.cityOfIssue': 'Città di rilascio',
      'document.country': 'Paese di rilascio (sigla, es. IT)',
      'address.address': 'Indirizzo',
      'address.postalCode': 'CAP',
      'address.city': 'Città',
      'address.region': 'Provincia o regione',
      'address.country': 'Paese (sigla, es. IT)',
      firstLevelApproverIdentifier: 'Approvatore di primo livello',
      secondLevelApproverIdentifier: 'Approvatore di secondo livello',
      acceptSalesConditions: 'Accetto le condizioni di vendita del fornitore'
    },
    hints: { phoneCountryCode: 'Es. +39' }
  },
  errors: {
    missingToken: 'Link del preventivo incompleto.',
    missingPin: 'Inserisci il codice di verifica.',
    notFound: 'Preventivo non trovato.',
    noLongerAvailable: 'Preventivo non più disponibile.',
    noPinIssued: 'Nessun codice da verificare: riapri il preventivo.',
    wrongPin: 'Codice non corretto.',
    internal: 'Si è verificato un errore. Riprova fra poco.',
    noSelection: 'Non hai selezionato nessuna offerta.',
    incompleteSelection:
      "Selezione incompleta: seleziona un'offerta per ogni servizio ricercato prima di proseguire.",
    duplicateService: 'Puoi scegliere una sola offerta per tratta.',
    notUnlocked: 'Preventivo non sbloccato: inserisci il codice di verifica.',
    alreadyAccepted: 'Preventivo già accettato o scaduto.',
    alreadyClosed: 'Preventivo già confermato o scaduto.',
    superseded:
      'Un altro preventivo di questa conversazione è già stato accettato. Non è più possibile prenotare da questo.',
    expired: 'Preventivo scaduto.',
    bookingStartFailed: 'Non è stato possibile avviare la prenotazione. Riprova fra poco.',
    missingBookingValues: 'Dati di prenotazione mancanti.',
    notAwaitingData: 'Il preventivo non è in attesa di dati.',
    // Esiti di una prenotazione non riuscita (`errorCode` scritto sul preventivo
    // dall'Executor). Le tariffe di viaggio vivono pochi minuti: la maggior parte di questi
    // casi significa "hai accettato troppo tardi", e la risposta utile è sempre la stessa —
    // rispondere al messaggio per avere offerte aggiornate.
    offerExpired:
      'Le offerte scelte sono scadute prima della prenotazione: rispondi al messaggio per riceverne di aggiornate.',
    outOfPolicy:
      'L’offerta scelta non è autorizzata dalla policy aziendale. La richiesta è passata a un operatore, che ti ricontatterà.',
    offerRejected:
      "Il fornitore non ha accettato l'offerta scelta. Rispondi al messaggio e cercheremo un'alternativa.",
    bookingFailed: 'La prenotazione non è andata a buon fine.',
    alreadyBooked: 'Questo viaggio risulta già prenotato.',
    partialFailure:
      'Solo una parte dei servizi è stata prenotata: la richiesta è passata a un operatore, che ti ricontatterà.',
    missingData:
      'Mancano ancora alcuni dati per completare la prenotazione. Un operatore ti ricontatterà per raccoglierli.',
    validationError:
      'Uno dei dati inseriti non è valido. La richiesta è passata a un operatore, che ti ricontatterà.',
    configurationError:
      'La prenotazione richiede una configurazione non disponibile al momento. La richiesta è passata a un operatore, che ti ricontatterà.',
    sessionExpired: 'La sessione di prenotazione è scaduta. Rispondi al messaggio per riprovare.',
    invalidRequest:
      'Si è verificato un problema tecnico nella prenotazione. La richiesta è passata a un operatore, che ti ricontatterà.',
    generic: (status) => `Errore ${status}`,
    network: 'Connessione non disponibile. Controlla la rete e riprova.'
  }
}

const en = {
  languageName: 'English',
  toolbar: {
    language: 'Language',
    toLight: 'Switch to light theme',
    toDark: 'Switch to dark theme'
  },
  header: {
    title: 'Travel quote',
    subtitle: 'Powered by Nora',
    validUntil: 'Valid until',
    atHour: 'at'
  },
  loading: 'Loading your quote…',
  unavailable: {
    title: 'Quote not available',
    text: 'We could not complete the request.',
    hint: 'If you need assistance, please contact your travel agency.'
  },
  deadline: {
    label: 'Time left to book:',
    hint: 'fares stay available for a few minutes only'
  },
  expired: {
    title: 'Quote expired',
    text: 'Travel fares can only be booked for a few minutes after the search, and this quote is past that time.',
    hint: 'Reply to the message from Nora and we will prepare an updated one right away.'
  },
  footnote: 'Offers are subject to availability: prices may change at the time of booking.',
  steps: {
    aria: 'Booking progress',
    choose: '1. Choose services',
    data: '2. Required details',
    confirm: '3. Confirmation'
  },
  pin: {
    title: 'Enter your verification code',
    text: 'We sent you a 5-digit code. Enter it to open the quote.',
    inputLabel: 'Verification code',
    attemptsLeft: (n) => `Attempts left: ${n}`,
    verifying: 'Checking…',
    submit: 'Open the quote'
  },
  services: {
    air: 'Flights',
    rail: 'Trains',
    hotel: 'Hotels',
    car: 'Cars',
    offers: (n) => `${n} ${n === 1 ? 'offer' : 'offers'}`,
    noChoice: 'nothing selected',
    leg: (n) => `Leg ${n}`
  },
  offer: {
    select: 'Select',
    selected: '✓ Selected',
    deselectAria: (summary) => `Deselect ${summary}`,
    selectAria: (summary) => `Select ${summary}`,
    refundable: 'Refundable',
    nonRefundable: 'Non-refundable',
    distanceFromSearch: (km) => `${km} km from search point`,
    rating: (value) => `Rating ${value}/10`,
    outbound: 'Outbound',
    return: 'Return',
    perDay: 'per day',
    moreDetails: 'Details',
    lessDetails: 'Hide details',
    cabin: 'Cabin',
    fare: 'Fare',
    carrier: 'Carrier',
    stop: 'stop',
    stops: 'stops',
    via: 'via',
    change: 'change',
    changes: 'changes',
    refundableLabel: 'Refundability',
    exchangeableLabel: 'Exchangeable',
    yes: 'Yes',
    no: 'No',
    totalDuration: 'Total duration',
    address: 'Address',
    mealPlan: 'Meal plan',
    ratingLabel: 'Rating',
    rentalCompany: 'Company',
    pickupStation: 'Pick-up',
    returnStation: 'Drop-off',
    transmission: 'Transmission',
    fuel: 'Fuel',
    airConditioning: 'Air conditioning',
    route: 'Route',
    pickup: 'Pick-up',
    // Detail panel sections
    sectionItinerary: 'Itinerary',
    sectionFare: 'Fare and conditions',
    sectionProperty: 'Property',
    sectionRoom: 'Room',
    sectionServices: 'Amenities',
    sectionVehicle: 'Vehicle',
    sectionStations: 'Pick-up and return',
    sectionPrice: 'Price',
    // Highlighted signals
    separateTickets: 'Separate tickets',
    separateTicketsHint:
      'Booking this produces separate tickets: a delay on one leg does not protect the other.',
    mixedCabin: 'Mixed cabin',
    mixedCabinHint: 'The flights on this itinerary are not all in the same class.',
    lowCost: 'Low cost',
    favourite: 'Preferred property',
    accessibleRoom: 'Accessible room',
    // Air and rail
    operatedBy: (carrier) => `operated by ${carrier}`,
    layover: (duration) => `${duration} layover`,
    changeAt: 'Change at',
    wifiOnBoard: 'Wi-Fi on board',
    provider: 'Provider',
    cancellationPolicy: 'Cancellation',
    // Hotel
    chain: 'Chain',
    roomType: 'Room type',
    reviews: (n) => `${n} reviews`,
    localTax: 'Local taxes',
    localTaxHint: 'Payable at the property, on top of the price shown.',
    ratingCleanliness: 'Cleanliness',
    ratingStaffService: 'Staff',
    ratingAmenities: 'Amenities',
    ratingPropertyConditions: 'Condition',
    distanceFromAnchor: (km) => `${km} km from the requested spot`,
    // Car
    equipment: 'Equipment',
    perDayShort: 'day',
    days: (n) => (n === 1 ? '1 day' : `${n} days`),
    priceBreakdown: (perDay, days) => `${perDay} × ${days}`,
    estimatedTotal: 'Estimated total'
  },
  tags: {
    bestPrice: 'Best price',
    closest: 'Fastest',
    bestBuy: 'Best buy',
    nonCompliant: '⚠ Out of policy'
  },
  cart: {
    title: 'Your trip',
    empty: 'Pick the services you want to book: one per leg, booked together as a single trip.',
    segment: { Air: 'Flight', Rail: 'Train', Hotel: 'Hotel', Car: 'Car' },
    total: 'Total',
    submitting: 'Sending…',
    continueWith: (n) => `Continue with ${n} services`,
    continueOne: 'Continue with the selected service',
    continue: 'Continue',
    missingLegs: (n) => `Select ${n} more ${n === 1 ? 'service' : 'services'} to continue.`,
    note: 'The supplier may ask for further details before confirming.',
    taxesExcluded: 'The total excludes local taxes, payable at the property.'
  },
  waiting: {
    preparingTitle: 'We are preparing your booking',
    preparingText:
      'One more step: if the supplier requires further details, the form will appear below.',
    finalizingTitle: 'We are confirming your booking',
    finalizingText: 'Almost there: as soon as the supplier replies you will see the trip code.',
    preparingSteps: ['Selected services sent', 'Creating the trip', 'Checking required details'],
    finalizingSteps: ['Details sent', 'Confirmation with the supplier', 'Trip code'],
    stalledTitle: 'This is taking longer than expected',
    stalledText:
      'The booking is still in progress and does not need to be redone. You can leave this page open or reopen the link later: the outcome will appear here.',
    stalledHint: 'If nothing changes in the next few minutes, contact your travel agency.'
  },
  result: {
    pendingTitle: 'Booking awaiting approval',
    pendingText: 'The request has been submitted for company approval.',
    confirmedTitle: 'Booking confirmed',
    confirmedText: 'Your trip has been booked successfully.',
    tripCode: 'Trip code:',
    openPortal: 'View the trip in the portal'
  },
  form: {
    introTitle: 'One more step',
    introText: (n) =>
      `The supplier requires ${n === 1 ? 'one detail' : `${n} details`} to issue this booking. Fill in the fields below and confirm: the trip is not booked yet.`,
    selectPlaceholder: 'Select…',
    missing: (fields) => `Please fill in the required fields: ${fields}.`,
    submitting: 'Sending…',
    submit: 'Confirm booking',
    groups: {
      traveller: 'Traveller details',
      passport: 'Passport',
      identity: 'Identity document',
      drivingLicence: 'Driving licence',
      address: 'Address',
      company: 'Company details',
      conditions: 'Terms of sale'
    },
    fields: {
      dateOfBirth: 'Date of birth',
      title: 'Title',
      nationality: 'Nationality (code, e.g. IT)',
      phoneCountryCode: 'International dialling code',
      phoneNumber: 'Phone number (without dialling code)',
      selectedPassportNo: 'Passport to use',
      'document.documentNumber': 'Document number',
      'document.documentHolder': 'Holder name as printed on the document',
      'document.effectiveDate': 'Issue date',
      'document.expiryDate': 'Expiry date',
      'document.cityOfIssue': 'City of issue',
      'document.country': 'Country of issue (code, e.g. IT)',
      'address.address': 'Address',
      'address.postalCode': 'Postcode',
      'address.city': 'City',
      'address.region': 'Province or region',
      'address.country': 'Country (code, e.g. IT)',
      firstLevelApproverIdentifier: 'First-level approver',
      secondLevelApproverIdentifier: 'Second-level approver',
      acceptSalesConditions: 'I accept the supplier’s terms of sale'
    },
    hints: { phoneCountryCode: 'e.g. +44' }
  },
  errors: {
    missingToken: 'The quote link is incomplete.',
    missingPin: 'Enter the verification code.',
    notFound: 'Quote not found.',
    noLongerAvailable: 'This quote is no longer available.',
    noPinIssued: 'No code to verify: please reopen the quote.',
    wrongPin: 'Wrong code.',
    internal: 'Something went wrong. Please try again shortly.',
    noSelection: 'You have not selected any offer.',
    incompleteSelection:
      'Incomplete selection: please select one offer for each searched service before continuing.',
    duplicateService: 'You can only choose one offer per leg.',
    notUnlocked: 'Quote locked: enter the verification code.',
    alreadyAccepted: 'This quote has already been accepted or has expired.',
    alreadyClosed: 'This quote has already been confirmed or has expired.',
    superseded:
      'Another quote from this conversation has already been accepted. This one can no longer be booked.',
    expired: 'This quote has expired.',
    bookingStartFailed: 'We could not start the booking. Please try again shortly.',
    missingBookingValues: 'Booking details missing.',
    notAwaitingData: 'This quote is not waiting for details.',
    offerExpired:
      'The selected offers expired before the booking: reply to the message to get updated ones.',
    outOfPolicy:
      'The selected offer is not allowed by your company policy. Your request has been passed to an agent, who will get back to you.',
    offerRejected:
      'The provider did not accept the selected offer. Reply to the message and we will look for an alternative.',
    bookingFailed: 'The booking did not go through.',
    alreadyBooked: 'This trip is already booked.',
    partialFailure:
      'Only part of the services was booked: your request has been passed to an agent, who will get back to you.',
    missingData:
      'Some details are still needed to complete the booking. An agent will contact you to collect them.',
    validationError:
      'One of the details provided is not valid. Your request has been passed to an agent, who will get back to you.',
    configurationError:
      'The booking requires a configuration that is currently unavailable. Your request has been passed to an agent, who will get back to you.',
    sessionExpired: 'The booking session has expired. Reply to the message to try again.',
    invalidRequest:
      'A technical issue occurred during the booking. Your request has been passed to an agent, who will get back to you.',
    generic: (status) => `Error ${status}`,
    network: 'No connection. Check your network and try again.'
  }
}

const fr = {
  languageName: 'Français',
  toolbar: {
    language: 'Langue',
    toLight: 'Passer au thème clair',
    toDark: 'Passer au thème sombre'
  },
  header: {
    title: 'Devis de voyage',
    subtitle: 'Powered by Nora',
    validUntil: "Valable jusqu'au",
    atHour: 'à'
  },
  loading: 'Chargement du devis…',
  unavailable: {
    title: 'Devis non disponible',
    text: "Il n'a pas été possible de compléter la demande.",
    hint: "Si vous avez besoin d'assistance, contactez votre agence de voyage."
  },
  deadline: {
    label: 'Temps pour réserver :',
    hint: 'les tarifs restent disponibles quelques minutes seulement'
  },
  expired: {
    title: 'Devis expiré',
    text: 'Les tarifs de voyage ne restent réservables que quelques minutes après la recherche, et ce devis a dépassé ce délai.',
    hint: 'Répondez au message de Nora et nous en préparerons un nouveau immédiatement.'
  },
  footnote:
    'Les offres sont soumises à disponibilité : les prix peuvent varier au moment de la réservation.',
  steps: {
    aria: 'Avancement de la réservation',
    choose: '1. Choisissez les services',
    data: '2. Données requises',
    confirm: '3. Confirmation'
  },
  pin: {
    title: 'Entrez le code de vérification',
    text: 'Nous vous avons envoyé un code à 5 chiffres. Saisissez-le pour ouvrir le devis.',
    inputLabel: 'Code de vérification',
    attemptsLeft: (n) => `Tentatives restantes : ${n}`,
    verifying: 'Vérification…',
    submit: 'Ouvrir le devis'
  },
  services: {
    air: 'Vols',
    rail: 'Trains',
    hotel: 'Hôtels',
    car: 'Voitures',
    offers: (n) => `${n} ${n === 1 ? 'offre' : 'offres'}`,
    noChoice: 'aucune sélection',
    leg: (n) => `Trajet ${n}`
  },
  offer: {
    select: 'Sélectionner',
    selected: '✓ Sélectionné',
    deselectAria: (summary) => `Désélectionner ${summary}`,
    selectAria: (summary) => `Sélectionner ${summary}`,
    refundable: 'Remboursable',
    nonRefundable: 'Non remboursable',
    distanceFromSearch: (km) => `${km} km du point de recherche`,
    rating: (value) => `Note ${value}/10`,
    outbound: 'Aller',
    return: 'Retour',
    perDay: 'par jour',
    moreDetails: 'Détails',
    lessDetails: 'Masquer les détails',
    cabin: 'Cabine',
    fare: 'Tarif',
    carrier: 'Compagnie',
    stop: 'escale',
    stops: 'escales',
    via: 'via',
    change: 'correspondance',
    changes: 'correspondances',
    refundableLabel: 'Remboursabilité',
    exchangeableLabel: 'Modifiable',
    yes: 'Oui',
    no: 'Non',
    totalDuration: 'Durée totale',
    address: 'Adresse',
    mealPlan: 'Pension',
    ratingLabel: 'Évaluation',
    rentalCompany: 'Compagnie',
    pickupStation: 'Retrait',
    returnStation: 'Restitution',
    transmission: 'Transmission',
    fuel: 'Carburant',
    airConditioning: 'Climatisation',
    route: 'Trajet',
    pickup: 'Retrait',
    // Sections du panneau de détails
    sectionItinerary: 'Itinéraire',
    sectionFare: 'Tarif et conditions',
    sectionProperty: 'Établissement',
    sectionRoom: 'Chambre',
    sectionServices: 'Services',
    sectionVehicle: 'Véhicule',
    sectionStations: 'Retrait et restitution',
    sectionPrice: 'Prix',
    // Signaux mis en avant
    separateTickets: 'Billets séparés',
    separateTicketsHint:
      "La réservation produit des billets distincts : un retard sur un trajet ne protège pas l'autre.",
    mixedCabin: 'Cabine mixte',
    mixedCabinHint: 'Les vols de cet itinéraire ne sont pas tous dans la même classe.',
    lowCost: 'Low cost',
    favourite: 'Établissement préféré',
    accessibleRoom: 'Chambre accessible',
    // Avion et train
    operatedBy: (carrier) => `opéré par ${carrier}`,
    layover: (duration) => `escale ${duration}`,
    changeAt: 'Changement à',
    wifiOnBoard: 'Wi-Fi à bord',
    provider: 'Fournisseur',
    cancellationPolicy: 'Annulation',
    // Hôtel
    chain: 'Chaîne',
    roomType: 'Type de chambre',
    reviews: (n) => `${n} avis`,
    localTax: 'Taxes locales',
    localTaxHint: 'À payer sur place, en plus du prix affiché.',
    ratingCleanliness: 'Propreté',
    ratingStaffService: 'Personnel',
    ratingAmenities: 'Services',
    ratingPropertyConditions: 'État',
    distanceFromAnchor: (km) => `${km} km du point demandé`,
    // Voiture
    equipment: 'Équipement',
    perDayShort: 'jour',
    days: (n) => (n === 1 ? '1 jour' : `${n} jours`),
    priceBreakdown: (perDay, days) => `${perDay} × ${days}`,
    estimatedTotal: 'Total estimé'
  },
  tags: {
    bestPrice: 'Meilleur prix',
    closest: 'Plus rapide',
    bestBuy: 'Best buy',
    nonCompliant: '⚠ Hors politique'
  },
  cart: {
    title: 'Votre voyage',
    empty:
      'Choisissez les services que vous souhaitez réserver : un par trajet, et nous les réservons ensemble.',
    segment: { Air: 'Vol', Rail: 'Train', Hotel: 'Hôtel', Car: 'Voiture' },
    total: 'Total',
    submitting: 'Envoi en cours…',
    continueWith: (n) => `Continuer avec ${n} services`,
    continueOne: 'Continuer avec le service choisi',
    continue: 'Continuer',
    missingLegs: (n) =>
      `Sélectionnez encore ${n} ${n === 1 ? 'service' : 'services'} pour continuer.`,
    note: "Le fournisseur pourrait demander d'autres informations avant de confirmer.",
    taxesExcluded: 'Le total ne comprend pas les taxes locales, à payer sur place.'
  },
  waiting: {
    preparingTitle: 'Nous préparons votre réservation',
    preparingText:
      "Encore une étape : si le fournisseur demande d'autres données, le formulaire apparaîtra ci-dessous.",
    finalizingTitle: 'Nous confirmons votre réservation',
    finalizingText:
      'Nous y sommes presque : dès que le fournisseur répondra, vous verrez le code voyage.',
    preparingSteps: [
      'Services choisis envoyés',
      'Création du voyage',
      'Vérification des données requises'
    ],
    finalizingSteps: ['Données envoyées', 'Confirmation auprès du fournisseur', 'Code voyage'],
    stalledTitle: 'Cela prend plus de temps que prévu',
    stalledText:
      "La réservation est toujours en cours et il n'est pas nécessaire de la refaire. Vous pouvez laisser cette page ouverte ou rouvrir le lien plus tard : le résultat apparaîtra ici.",
    stalledHint: 'Si rien ne change dans les prochaines minutes, contactez votre agence de voyage.'
  },
  result: {
    pendingTitle: "Réservation en attente d'approbation",
    pendingText: "La demande a été transmise pour approbation de l'entreprise.",
    confirmedTitle: 'Réservation confirmée',
    confirmedText: 'Le voyage a été réservé avec succès.',
    tripCode: 'Code voyage :',
    openPortal: 'Voir le voyage dans le portail'
  },
  form: {
    introTitle: 'Encore une étape',
    introText: (n) =>
      `Le fournisseur demande ${n === 1 ? 'une donnée' : `${n} données`} pour émettre cette réservation. Remplissez les champs ci-dessous et confirmez : le voyage n'est pas encore réservé.`,
    selectPlaceholder: 'Sélectionner…',
    missing: (fields) => `Veuillez remplir les champs obligatoires : ${fields}.`,
    submitting: 'Envoi en cours…',
    submit: 'Confirmer la réservation',
    groups: {
      traveller: 'Données du voyageur',
      passport: 'Passeport',
      identity: "Pièce d'identité",
      drivingLicence: 'Permis de conduire',
      address: 'Adresse',
      company: "Données de l'entreprise",
      conditions: 'Conditions de vente'
    },
    fields: {
      dateOfBirth: 'Date de naissance',
      title: 'Titre',
      nationality: 'Nationalité (code, ex. FR)',
      phoneCountryCode: 'Indicatif international',
      phoneNumber: 'Numéro de téléphone (sans indicatif)',
      selectedPassportNo: 'Passeport à utiliser',
      'document.documentNumber': 'Numéro du document',
      'document.documentHolder': "Titulaire tel qu'indiqué sur le document",
      'document.effectiveDate': 'Date de délivrance',
      'document.expiryDate': "Date d'expiration",
      'document.cityOfIssue': 'Ville de délivrance',
      'document.country': 'Pays de délivrance (code, ex. FR)',
      'address.address': 'Adresse',
      'address.postalCode': 'Code postal',
      'address.city': 'Ville',
      'address.region': 'Région ou département',
      'address.country': 'Pays (code, ex. FR)',
      firstLevelApproverIdentifier: 'Approbateur de premier niveau',
      secondLevelApproverIdentifier: 'Approbateur de second niveau',
      acceptSalesConditions: "J'accepte les conditions de vente du fournisseur"
    },
    hints: { phoneCountryCode: 'Ex. +33' }
  },
  errors: {
    missingToken: 'Le lien du devis est incomplet.',
    missingPin: 'Entrez le code de vérification.',
    notFound: 'Devis non trouvé.',
    noLongerAvailable: "Ce devis n'est plus disponible.",
    noPinIssued: 'Aucun code à vérifier : veuillez rouvrir le devis.',
    wrongPin: 'Code incorrect.',
    internal: "Une erreur s'est produite. Veuillez réessayer dans quelques instants.",
    noSelection: "Vous n'avez sélectionné aucune offre.",
    incompleteSelection:
      'Sélection incomplète : veuillez sélectionner une offre pour chaque service recherché avant de continuer.',
    duplicateService: "Vous ne pouvez choisir qu'une seule offre par trajet.",
    notUnlocked: 'Devis verrouillé : entrez le code de vérification.',
    alreadyAccepted: 'Ce devis a déjà été accepté ou a expiré.',
    alreadyClosed: 'Ce devis a déjà été confirmé ou a expiré.',
    superseded:
      "Un autre devis de cette conversation a déjà été accepté. Il n'est plus possible de réserver à partir de celui-ci.",
    expired: 'Ce devis a expiré.',
    bookingStartFailed:
      "Il n'a pas été possible de démarrer la réservation. Veuillez réessayer dans quelques instants.",
    missingBookingValues: 'Données de réservation manquantes.',
    notAwaitingData: "Ce devis n'est pas en attente de données.",
    offerExpired:
      'Les offres sélectionnées ont expiré avant la réservation : répondez au message pour en recevoir de nouvelles.',
    outOfPolicy:
      "L'offre choisie n'est pas autorisée par la politique de l'entreprise. La demande a été transmise à un opérateur, qui vous recontactera.",
    offerRejected:
      "Le fournisseur n'a pas accepté l'offre choisie. Répondez au message et nous chercherons une alternative.",
    bookingFailed: "La réservation n'a pas abouti.",
    alreadyBooked: 'Ce voyage est déjà réservé.',
    partialFailure:
      'Seule une partie des services a été réservée : la demande a été transmise à un opérateur, qui vous recontactera.',
    missingData:
      'Certaines données sont encore nécessaires pour finaliser la réservation. Un opérateur vous recontactera pour les recueillir.',
    validationError:
      "L'une des données saisies n'est pas valide. La demande a été transmise à un opérateur, qui vous recontactera.",
    configurationError:
      'La réservation nécessite une configuration actuellement indisponible. La demande a été transmise à un opérateur, qui vous recontactera.',
    sessionExpired: 'La session de réservation a expiré. Répondez au message pour réessayer.',
    invalidRequest:
      'Un problème technique est survenu lors de la réservation. La demande a été transmise à un opérateur, qui vous recontactera.',
    generic: (status) => `Erreur ${status}`,
    network: 'Connexion non disponible. Vérifiez votre réseau et réessayez.'
  }
}

export const quoteTranslations = { it, en, fr }
